import { useMemo, useState } from 'react'
import { t } from '@/i18n'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface CalendarProps {
  selected?: Date
  onSelect: (date: Date) => void
  /** Inclusive lower bound. */
  minDate?: Date
  /** Inclusive upper bound. */
  maxDate?: Date
  /** Initial month displayed when no value is selected. */
  defaultMonth?: Date
  className?: string
}

const WEEK_STARTS_ON = 1 // Monday

export function Calendar({
  selected,
  onSelect,
  minDate,
  maxDate,
  defaultMonth,
  className,
}: CalendarProps) {
  const [viewMonth, setViewMonth] = useState<Date>(
    () => startOfDay(selected ?? defaultMonth ?? new Date())
  )

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth)
    const monthEnd = endOfMonth(viewMonth)
    const gridStart = startOfWeek(monthStart, { weekStartsOn: WEEK_STARTS_ON })
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON })
    return eachDayOfInterval({ start: gridStart, end: gridEnd })
  }, [viewMonth])

  const weekdayLabels = useMemo(() => {
    // Localized 2-letter abbreviations (lu, ma, mi, ju, vi, sá, do) starting from WEEK_STARTS_ON
    const base = startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON })
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return format(d, 'EEEEEE', { locale: es })
    })
  }, [])

  const isDisabled = (d: Date) => {
    if (minDate && isBefore(startOfDay(d), startOfDay(minDate))) return true
    if (maxDate && isAfter(startOfDay(d), startOfDay(maxDate))) return true
    return false
  }

  const goPrev = () => setViewMonth((m) => subMonths(m, 1))
  const goNext = () => setViewMonth((m) => addMonths(m, 1))
  const goToday = () => setViewMonth(startOfDay(new Date()))

  const monthLabel = format(viewMonth, 'MMMM yyyy', { locale: es })
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)

  return (
    <div className={cn('p-3 select-none', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={goPrev}
          aria-label={t('common.prevMonth')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <button
          type="button"
          onClick={goToday}
          className="text-sm font-medium capitalize hover:text-primary transition-colors"
          title={t('common.goToday')}
        >
          {monthLabelCap}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={goNext}
          aria-label={t('common.nextMonth')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {weekdayLabels.map((d, i) => (
          <div
            key={i}
            className="text-[11px] font-medium text-center text-muted-foreground capitalize py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const inMonth = isSameMonth(day, viewMonth)
          const isSelected = selected ? isSameDay(day, selected) : false
          const disabled = isDisabled(day)
          const today = isToday(day)
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(day)}
              className={cn(
                'h-9 w-9 rounded-md text-sm transition-colors',
                'flex items-center justify-center',
                inMonth ? 'text-foreground' : 'text-muted-foreground/40',
                !disabled && !isSelected && 'hover:bg-accent hover:text-accent-foreground',
                today && !isSelected && 'ring-1 ring-primary/40',
                isSelected && 'bg-primary text-primary-foreground hover:bg-primary',
                disabled && 'opacity-40 cursor-not-allowed'
              )}
              aria-pressed={isSelected}
              aria-label={format(day, "d 'de' MMMM yyyy", { locale: es })}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
    </div>
  )
}
